import { useState, useEffect } from 'react';
import {
  isScheduledMarketOpen,
  getSecondsUntilOpen,
} from '../../../config/tradingSchedules';

/**
 * Generic countdown hook for any scheduled market.
 *
 * Drop-in replacement for the per-asset countdown hooks
 * (useMarketOpenCountdown, useWatchMarketCountdown, etc.)
 * but driven by a `tradingSchedule` key from the market config.
 *
 * @param {string|null|undefined} scheduleType  One of TRADING_SCHEDULES values
 * @returns {{ isMarketOpen: boolean, timeRemaining: { hours: number, minutes: number, seconds: number } }}
 */
const useScheduledMarketCountdown = (scheduleType) => {
  const [isOpen, setIsOpen] = useState(() => isScheduledMarketOpen(scheduleType));
  const [timeRemaining, setTimeRemaining] = useState({ hours: 0, minutes: 0, seconds: 0 });

  useEffect(() => {
    // No schedule → always open, no timer needed
    if (!scheduleType) {
      setIsOpen(true);
      setTimeRemaining({ hours: 0, minutes: 0, seconds: 0 });
      return;
    }

    const tick = () => {
      const open = isScheduledMarketOpen(scheduleType);
      setIsOpen(open);

      if (open) {
        setTimeRemaining({ hours: 0, minutes: 0, seconds: 0 });
      } else {
        const total = getSecondsUntilOpen(scheduleType);
        setTimeRemaining({
          hours: Math.floor(total / 3600),
          minutes: Math.floor((total % 3600) / 60),
          seconds: total % 60,
        });
      }
    };

    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [scheduleType]);

  return { isMarketOpen: isOpen, timeRemaining };
};

export default useScheduledMarketCountdown;
